import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  Building2, Calendar, Clock, ChevronLeft, ChevronRight, CheckCircle,
  HelpCircle, Save, Send, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VisitaFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visita, setVisita] = useState(null);
  const [formulario, setFormulario] = useState(null);
  const [respostas, setRespostas] = useState({});
  const [observacoesVisitador, setObservacoesVisitador] = useState('');
  const [currentDimensaoIndex, setCurrentDimensaoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadVisita();
  }, [id]);

  const loadVisita = async () => {
    const token = localStorage.getItem('visitare_token');
    if (!token) {
      navigate('/visitare/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/visitare/visitas/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Visita não encontrada');

      const data = await response.json();
      setVisita(data);
      setFormulario(data.formulario);
      
      // Initialize respostas from existing data
      const existingRespostas = {};
      (data.respostas || []).forEach(r => {
        existingRespostas[r.criterio_id] = {
          resposta_valor: r.resposta_valor,
          observacao: r.observacao || ''
        };
      });
      setRespostas(existingRespostas);
      setObservacoesVisitador(data.observacoes_visitador || '');
    } catch (error) {
      toast.error(error.message);
      navigate('/visitare/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleIniciar = async () => {
    const token = localStorage.getItem('visitare_token');
    try {
      await fetch(`${API_URL}/api/visitare/visitas/${id}/iniciar`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setVisita(prev => ({ ...prev, status: 'Em Curso' }));
      toast.success('Visita iniciada');
    } catch (error) {
      toast.error('Erro ao iniciar visita');
    }
  };

  const handleRespostaChange = (criterioId, value, field = 'resposta_valor') => {
    setRespostas(prev => ({
      ...prev,
      [criterioId]: {
        ...prev[criterioId],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    const currentDimensao = formulario?.dimensoes?.[currentDimensaoIndex];
    const missingRequired = currentDimensao?.criterios?.filter(c => 
      c.obrigatorio && !respostas[c.id]?.resposta_valor
    );

    if (missingRequired?.length > 0) {
      toast.error(`Preencha todos os campos obrigatórios`);
      return;
    }

    // If not last dimension, go to next
    if (currentDimensaoIndex < (formulario?.dimensoes?.length || 0) - 1) {
      setCurrentDimensaoIndex(prev => prev + 1);
      window.scrollTo(0, 0);
      return;
    }

    // Submit
    setSubmitting(true);
    const token = localStorage.getItem('visitare_token');

    try {
      const respostasArray = Object.entries(respostas).map(([criterio_id, data]) => ({
        criterio_id,
        resposta_valor: data.resposta_valor,
        observacao: data.observacao
      }));

      const response = await fetch(`${API_URL}/api/visitare/visitas/${id}/submeter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          respostas: respostasArray,
          observacoes_visitador: observacoesVisitador,
          data_realizacao: new Date().toISOString().split('T')[0]
        })
      });

      if (!response.ok) throw new Error('Erro ao submeter');

      const result = await response.json();
      toast.success(`Visita submetida! Score: ${result.score_final}`);
      navigate('/visitare/dashboard');
    } catch (error) {
      toast.error('Erro ao submeter visita');
    } finally {
      setSubmitting(false);
    }
  };

  const renderResposta = (criterio) => {
    const value = respostas[criterio.id]?.resposta_valor;

    switch (criterio.tipo_resposta) {
      case 'escala_5':
        return (
          <div className="space-y-2">
            <RadioGroup
              value={String(value || '')}
              onValueChange={v => handleRespostaChange(criterio.id, parseInt(v))}
              className="flex gap-2"
            >
              {[1, 2, 3, 4, 5].map(n => (
                <div key={n} className="flex flex-col items-center">
                  <RadioGroupItem value={String(n)} id={`${criterio.id}-${n}`} className="sr-only" />
                  <label
                    htmlFor={`${criterio.id}-${n}`}
                    className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all ${
                      value === n 
                        ? 'bg-[#0066CC] border-[#0066CC] text-white' 
                        : 'border-slate-200 hover:border-[#0066CC]'
                    }`}
                  >
                    {n}
                  </label>
                </div>
              ))}
            </RadioGroup>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Insuficiente</span>
              <span>Excelente</span>
            </div>
          </div>
        );

      case 'escala_10':
        return (
          <div className="space-y-2">
            <RadioGroup
              value={String(value || '')}
              onValueChange={v => handleRespostaChange(criterio.id, parseInt(v))}
              className="flex gap-1 flex-wrap"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <div key={n}>
                  <RadioGroupItem value={String(n)} id={`${criterio.id}-${n}`} className="sr-only" />
                  <label
                    htmlFor={`${criterio.id}-${n}`}
                    className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center cursor-pointer text-sm transition-all ${
                      value === n 
                        ? 'bg-[#0066CC] border-[#0066CC] text-white' 
                        : 'border-slate-200 hover:border-[#0066CC]'
                    }`}
                  >
                    {n}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 'sim_nao':
        return (
          <RadioGroup
            value={value === true ? 'sim' : value === false ? 'nao' : ''}
            onValueChange={v => handleRespostaChange(criterio.id, v === 'sim')}
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="sim" id={`${criterio.id}-sim`} />
              <Label htmlFor={`${criterio.id}-sim`} className="cursor-pointer">Sim</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="nao" id={`${criterio.id}-nao`} />
              <Label htmlFor={`${criterio.id}-nao`} className="cursor-pointer">Não</Label>
            </div>
          </RadioGroup>
        );

      case 'numerico':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={e => handleRespostaChange(criterio.id, parseFloat(e.target.value) || null)}
            placeholder="Introduza um número"
            className="max-w-[200px]"
          />
        );

      case 'texto':
      default:
        return (
          <Textarea
            value={value || ''}
            onChange={e => handleRespostaChange(criterio.id, e.target.value)}
            placeholder="Introduza a sua resposta"
            rows={3}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentDimensao = formulario?.dimensoes?.[currentDimensaoIndex];
  const totalDimensoes = formulario?.dimensoes?.length || 0;
  const progress = ((currentDimensaoIndex + 1) / totalDimensoes) * 100;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 lg:p-6">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <Button variant="ghost" onClick={() => navigate('/visitare/dashboard')} className="mb-4">
          <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#0066CC]/10 rounded-xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-[#0066CC]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">{visita?.instituicao_nome}</h1>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {visita?.data_agendada}
                    </span>
                    <Badge variant={visita?.status === 'Em Curso' ? 'default' : 'secondary'}>
                      {visita?.status}
                    </Badge>
                  </div>
                </div>
              </div>
              {visita?.status === 'Agendada' && (
                <Button onClick={handleIniciar} className="bg-[#0066CC]">
                  <Clock className="w-4 h-4 mr-2" /> Iniciar Visita
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-500">Progresso</span>
          <span className="text-sm font-medium">{currentDimensaoIndex + 1} de {totalDimensoes}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Form */}
      {visita?.status !== 'Agendada' && currentDimensao && (
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="outline" className="mb-2">{currentDimensao.codigo}</Badge>
                  <CardTitle>{currentDimensao.nome}</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">{currentDimensao.descricao}</p>
                </div>
                <Badge variant="secondary">Peso: {currentDimensao.peso}%</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {currentDimensao.criterios?.map((criterio, index) => (
                <div key={criterio.id} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="w-8 h-8 bg-[#0066CC] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium">{criterio.questao}</p>
                      {criterio.obrigatorio && (
                        <span className="text-xs text-red-500">* Obrigatório</span>
                      )}
                      {criterio.ajuda && (
                        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                          <HelpCircle className="w-3 h-3" /> {criterio.ajuda}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">{criterio.peso}%</Badge>
                  </div>

                  {renderResposta(criterio)}

                  <div className="mt-3">
                    <Label className="text-xs text-slate-500">Observações (opcional)</Label>
                    <Input
                      value={respostas[criterio.id]?.observacao || ''}
                      onChange={e => handleRespostaChange(criterio.id, e.target.value, 'observacao')}
                      placeholder="Notas adicionais..."
                      className="mt-1"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Last dimension - observações gerais */}
          {currentDimensaoIndex === totalDimensoes - 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Observações Gerais</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={observacoesVisitador}
                  onChange={e => setObservacoesVisitador(e.target.value)}
                  placeholder="Observações gerais sobre a visita..."
                  rows={4}
                />
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentDimensaoIndex(prev => prev - 1)}
              disabled={currentDimensaoIndex === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
            </Button>

            {currentDimensaoIndex < totalDimensoes - 1 ? (
              <Button onClick={handleSubmit} className="bg-[#0066CC]">
                Próxima <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>Submetendo...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Submeter Avaliação
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
